import { FieldValues } from "../Schema";
import { SchemaClient } from "./GraphqlClient";
import { SchemaClientKinds, Source } from "./Source";

/**
 * A {@link SchemaClient} that answers nothing.
 *
 * For a read path whose values already carry the text a user sees — the equipment exports
 * read from a search index, where every enum is stored resolved — there is nothing for the
 * option lists to do. The CSV translators fall back to the stored value when a value matches
 * no option, which is what makes answering nothing correct rather than merely tolerable, and
 * it keeps a cold start from making a dozen queries whose results would go unused.
 *
 * Never use this on a write path. There, an empty option list makes `DataManager` reduce
 * every value of the fields reading it to `EMPTY_VALUE`, and those nulls are then written.
 * A writer builds its schema with a real client and checks the sources it got back — see
 * `assertEquipmentSourcesAvailable`.
 *
 * Declared as `GRAPHQL` because that is the kind whose definitions every source carries; the
 * sources that are GraphQL-only would otherwise short-circuit on the kind rather than on the
 * empty answer, which is the same result reached for the wrong reason.
 */
export class NonQueryingClient implements SchemaClient {

  public readonly kind = SchemaClientKinds.GRAPHQL;

  /**
   * @returns Always `null`.
   */
  public async query<T, V = FieldValues>(
    _source: Source<T, V>,
    _variables?: V,
  ): Promise<T | null> {
    return null;
  }

  /**
   * @returns Always an empty list.
   */
  public async queryList<T, V = FieldValues>(
    _source: Source<T[], V>,
    _variables?: V,
  ): Promise<T[] | null> {
    return [];
  }
}

/** Shared instance. It holds no state, so one is enough. */
export const nonQueryingClient: SchemaClient = new NonQueryingClient();
